import React, { useEffect, useMemo, useState } from 'react';
import { domainApi } from '../utils/api';
import { domainSenderName } from '../utils/replyIdentity';
import { SENDER_HELPER_COPY, senderWaitLabel, senderWaitingOn } from '../utils/senderWaitState';
import DnsRecordsPanel from './DnsRecordsPanel';

const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const GOCTA_VALUE = 'gocta';

/**
 * Shared From-address picker + inline add.
 *
 * Error handling is a 1:1 port of CustomDomains.handleAddDomain (4478cfa):
 * - 422-reuse is a backend SUCCESS (reused:true / alreadyExisted). Do not treat it as failure.
 * - Failed add sets a persistent addError banner to the real API/Postmark message.
 * - Failed add MUST NOT clear the form fields (never silently revert).
 */
export default function SenderPicker({
  mode = 'picker',
  workspaceId,
  senders = [],
  selectedSenderId = '',
  senderMode = 'gocta',
  onSelect,
  onAdded,
  onCheckStatus,
  showToast,
  showGoctaOption = true,
}) {
  const defaultDomain = useMemo(() => {
    const verified = senders.find((s) => s.verificationStatus === 'verified');
    return (verified || senders[0] || {}).domain || '';
  }, [senders]);

  const [newDomain, setNewDomain] = useState(defaultDomain);
  const [newEmailPrefix, setNewEmailPrefix] = useState('replies');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [addError, setAddError] = useState('');
  const [dnsRecords, setDnsRecords] = useState(null);
  const [lastAdd, setLastAdd] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showAdd, setShowAdd] = useState(mode === 'manage');

  useEffect(() => {
    if (!newDomain && defaultDomain) setNewDomain(defaultDomain);
  }, [defaultDomain, newDomain]);

  const selectValue = senderMode === 'domain' && selectedSenderId ? selectedSenderId : GOCTA_VALUE;

  function handleSelectChange(e) {
    const value = e.target.value;
    if (value === GOCTA_VALUE) {
      onSelect?.({ mode: 'gocta', sender: null });
      return;
    }
    const sender = senders.find((s) => s.id === value);
    if (sender) onSelect?.({ mode: 'domain', sender });
  }

  async function checkEmailUniqueness(email) {
    const result = await domainApi.checkEmailUniqueness(email.toLowerCase().trim());
    if (result.error) {
      throw new Error(result.error);
    }
    return result.available !== false;
  }

  async function handleAddDomain() {
    if (!newDomain || !newEmailPrefix) {
      showToast?.('Please enter domain and email prefix', 'info');
      return;
    }
    if (!workspaceId) {
      setAddError('No workspace selected. Refresh and try again.');
      showToast?.('No workspace selected', 'error');
      return;
    }

    const fullEmail = `${newEmailPrefix.trim().toLowerCase()}@${newDomain.trim().toLowerCase()}`;

    if (!EMAIL_REGEX.test(fullEmail)) {
      setEmailError('Invalid email format');
      return;
    }

    setEmailError('');
    setAddError('');
    setAddingDomain(true);
    try {
      const isAvailable = await checkEmailUniqueness(fullEmail);
      if (!isAvailable) {
        const msg = `${fullEmail} is already in your workspace or another account. Use Edit on the existing card, or pick a different prefix.`;
        setEmailError(msg);
        setAddError(msg);
        showToast?.(msg, 'error');
        return;
      }

      const result = await domainApi.add({
        workspaceId,
        formId: null,
        domain: newDomain.trim().toLowerCase(),
        fromEmail: fullEmail,
        displayName: newDisplayName || newDomain,
      });

      if (result.success) {
        // 422-reuse lands here as success with reused:true — keep treating as add-ok.
        if (result.waitingOn === 'dns') {
          setDnsRecords(result.dnsRecords);
        } else {
          setDnsRecords(null);
        }
        setLastAdd(result);
        setNewDomain(defaultDomain);
        setNewEmailPrefix('replies');
        setNewDisplayName('');
        if (result.waitingOn === 'email_confirmation') {
          showToast?.('Sender added. Check the confirmation email Postmark sent — DNS is already in place on this domain.');
        } else if (result.waitingOn === 'none') {
          showToast?.(result.alreadyExisted ? 'That sender is already on this workspace.' : 'Domain verified.');
        } else {
          showToast?.('Domain added! Add the DNS records below to verify.');
        }
        onAdded?.(result);
      } else {
        const msg = result.error || 'Failed to add domain';
        setAddError(msg);
        showToast?.(msg, 'error');
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      const msg = error?.message || 'Failed to add domain';
      setAddError(msg);
      showToast?.(msg, 'error');
    } finally {
      setAddingDomain(false);
    }
  }

  async function handleCheckAdded() {
    const fromEmail = lastAdd?.domain?.fromEmail;
    const domainId = lastAdd?.domain?.id || lastAdd?.domainId;
    if (!fromEmail || !domainId) return;
    setChecking(true);
    try {
      const result = await domainApi.checkStatus(fromEmail);
      await onCheckStatus?.(domainId, fromEmail, result);
      setLastAdd((prev) => prev ? {
        ...prev,
        waitingOn: result.waitingOn,
        confirmed: result.confirmed,
      } : prev);
      if (result.waitingOn === 'none' || result.verified) {
        setDnsRecords(null);
      }
    } catch (error) {
      showToast?.('Error checking status: ' + (error.message || error), 'error');
    } finally {
      setChecking(false);
    }
  }

  const addedWaitingOn = lastAdd?.waitingOn;

  return (
    <div className="sender-picker">
      <p className="fb-muted" style={{ margin: '0 0 0.75rem', fontSize: '0.85rem' }}>
        {SENDER_HELPER_COPY}
      </p>

      {mode === 'picker' && (
        <div style={{ marginBottom: '1rem' }}>
          <label className="fb-label" htmlFor="sender-picker-select">From address</label>
          <select
            id="sender-picker-select"
            className="fb-select"
            value={showGoctaOption ? selectValue : (selectedSenderId || '')}
            onChange={handleSelectChange}
          >
            {showGoctaOption && (
              <option value={GOCTA_VALUE}>replies@gocta.ai (instant)</option>
            )}
            {senders.map((sender) => (
              <option key={sender.id} value={sender.id}>
                {sender.fromEmail} — {senderWaitLabel(sender)}
              </option>
            ))}
          </select>
          {senderMode === 'domain' && selectedSenderId && (() => {
            const selected = senders.find((s) => s.id === selectedSenderId);
            if (!selected) return null;
            const wait = senderWaitingOn(selected);
            const name = domainSenderName(selected);
            return (
              <p className="fb-muted" style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
                {name ? `"${name}" ` : ''}
                {wait === 'none' ? 'Ready to send.' : `${senderWaitLabel(selected)}.`}
              </p>
            );
          })()}
        </div>
      )}

      {mode === 'picker' && !showAdd && (
        <button
          type="button"
          className="fb-link-inline today-link-accent"
          onClick={() => setShowAdd(true)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginBottom: '1rem' }}
        >
          Add a new sender
        </button>
      )}

      {(mode === 'manage' || showAdd) && (
        <div className="domains-add-form">
          {mode === 'picker' && (
            <strong style={{ display: 'block', marginBottom: '0.75rem' }}>Add a new sender</strong>
          )}
          <div className="domains-field">
            <label className="domains-field-label">Domain</label>
            <input
              type="text"
              className="fb-input"
              placeholder="yourbusiness.com"
              value={newDomain}
              onChange={(e) => {
                setNewDomain(e.target.value);
                setEmailError('');
                setAddError('');
              }}
            />
          </div>

          <div className="domains-field">
            <label className="domains-field-label">Email Prefix (before @)</label>
            <input
              type="text"
              className={`fb-input${emailError ? ' fb-input-error' : ''}`}
              placeholder="replies"
              value={newEmailPrefix}
              onChange={(e) => {
                setNewEmailPrefix(e.target.value);
                setEmailError('');
                setAddError('');
              }}
            />
            <p className="domains-field-hint">
              Will become: {newEmailPrefix || 'replies'}@{newDomain || 'yourdomain.com'}
            </p>
            {emailError && (
              <p className="domains-field-error">{emailError}</p>
            )}
          </div>

          <div className="domains-field">
            <label className="domains-field-label">Display Name (optional)</label>
            <input
              type="text"
              className="fb-input"
              placeholder="Your Business Name"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
            />
          </div>

          {addError && (
            <div className="fb-callout warn" data-testid="sender-add-error" style={{ margin: 0 }}>
              <strong>Couldn’t add sender</strong>
              <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {addError}
              </p>
            </div>
          )}

          <button
            type="button"
            className="inbox-pill-btn-primary domains-add-btn"
            onClick={handleAddDomain}
            disabled={addingDomain || !newDomain || !newEmailPrefix}
          >
            {addingDomain ? 'Adding Domain...' : 'Add Domain'}
          </button>
        </div>
      )}

      {addedWaitingOn === 'email_confirmation' && (
        <div className="fb-callout warn" style={{ marginTop: '1rem' }}>
          <strong>Waiting on email confirmation</strong>
          <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0', lineHeight: 1.5 }}>
            DKIM and Return-Path are already verified on this domain. Open the Postmark confirmation email sent to {lastAdd?.domain?.fromEmail} and click the link. Then check status.
          </p>
          <button
            type="button"
            className="inbox-pill-btn-primary domains-add-btn"
            style={{ marginTop: '0.75rem' }}
            onClick={handleCheckAdded}
            disabled={checking}
          >
            {checking ? 'Checking...' : 'Check Verification Status'}
          </button>
        </div>
      )}

      {addedWaitingOn === 'none' && lastAdd && (
        <div className="fb-callout success" style={{ marginTop: '1rem' }}>
          <strong>Ready</strong>
          <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
            {lastAdd.domain?.fromEmail || 'This sender'} can send now.
          </p>
        </div>
      )}

      {dnsRecords && addedWaitingOn === 'dns' && (
        <div style={{ marginTop: '1rem' }}>
          <DnsRecordsPanel
            records={dnsRecords}
            includeSpf={mode === 'manage'}
            onCopy={(msg, tone) => showToast?.(msg, tone)}
            verifyButton={(
              <button
                type="button"
                className="inbox-pill-btn-primary domains-add-btn"
                style={{ marginTop: '0.75rem' }}
                onClick={handleCheckAdded}
                disabled={checking}
              >
                {checking ? 'Checking...' : 'Check Verification Status'}
              </button>
            )}
          />
        </div>
      )}
    </div>
  );
}
